//Built-in libraries
import { ChangeEvent, useEffect, useState } from "react";

//Third party libraries
import { v4 as uuidv4 } from "uuid";
import { useFormContext, useWatch } from "react-hook-form";
import {
  InformationCircleIcon,
  PencilAltIcon,
  PlusIcon,
  TrashIcon,
} from "@heroicons/react/outline";

//Custom components
import Confirm from "components/UI/Modal/Confirm";
import AddRealEstateGoalsForm from "./AddRealEstateGoalsForm";
import Button from "components/UI/Button";
import { HookRadioGroup } from "components/UI/HookForm/Form/RadioGroup";

//Services
import { useApplicationFormContext } from "features/ApplicationForm/ApplicationFormProvider";
import { initialValues } from "./constants";
import { validateInput } from "./helper";
import { isObjectEmpty } from "utils";
import { IInterestedProperty } from "./types";
import { ISignApplicationPublicAssetPayload } from "api/Rover/ApplicationForm/types";
import { SignedApplicationAssetResponse } from "types/Shared";
import { useFileUpload } from "hooks/useFileUpload";
import { signApplicationPublicAsset } from "api/Rover/ApplicationForm";
import { HookInput } from "components/UI/HookForm/Form/Input";

//Component Begin

export default function RealEstateGoals() {
  const { setCurrentStep, updateData, data } = useApplicationFormContext();
  const [showRemoveConfirm, setShowRemoveConfirm] = useState<boolean>(false);
  const [uploadError, setUploadError] = useState<string>("");
  const [showAddModal, setShowAddModal] = useState<boolean>(false);
  const [propertyAddress, setPropertyAddress] = useState(initialValues);
  const [selectedAddress, setSelectedAddress] = useState<IInterestedProperty>();
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editAddressId, setEditAddressId] = useState<string>("");
  const haveIdentifiedProperty = useWatch({
    name: "step2.q1",
  });
  const { register, setValue } = useFormContext();

  useEffect(() => {
    register("step2.interestedProperties", undefined);
  }, []);

  const removeAddressHandler = (address: IInterestedProperty) => {
    setShowRemoveConfirm(true);
    setSelectedAddress(address);
  };

  const confirmRemoveAddress = async () => {
    setShowRemoveConfirm(false);
    const filteredProperties = data.step2.interestedProperties.filter(
      (t: any) => t.id !== selectedAddress?.id
    );
    setValue("step2.interestedProperties", filteredProperties);
    updateData({
      ...data,
      step2: {
        ...data.step2,
        interestedProperties: filteredProperties,
      },
    });
  };

  const cancelRemoveAddress = () => {
    setShowRemoveConfirm(false);
  };

  const toggleAddModal = () => {
    resetForm();
    setIsEditing(false);
    setShowAddModal((prev) => !prev);
  };

  const resetForm = () => {
    setPropertyAddress(initialValues);
    setEditAddressId("");
  };

  const inputChangedHandler = (
    value:
      | string
      | number
      | boolean
      | ChangeEvent<HTMLInputElement>
      | undefined
      | null
      | FileList
      | string[],
    name: string
  ) => {
    const inputValidation = validateInput(propertyAddress[name].rules, value);

    setPropertyAddress((prevState) => ({
      ...prevState,
      [name]: {
        ...prevState[name],
        value,
        isTouched: true,
        error: inputValidation.error,
      },
    }));
  };

  const validateForm = () => {
    const formData = { ...propertyAddress };

    const formDataErrors = Object.keys(formData).reduce(
      (prev: { [key: string]: string }, next: string) => {
        if (formData[next].error.length >= 1) {
          return {
            ...prev,
            [next]: formData[next].error,
          };
        }
        if (!formData[next].isTouched && formData[next].rules.required) {
          setPropertyAddress((prevState) => ({
            ...prevState,
            [next]: { ...prevState[next], error: "Required" },
          }));
          return {
            ...prev,
            [next]: "Required",
          };
        }
        return { ...prev };
      },
      {}
    );

    return isObjectEmpty(formDataErrors);
  };

  const onAddAddressHandler = () => {
    if (validateForm()) {
      const formattedData = Object.keys(propertyAddress).reduce(
        (
          prev: {
            [key: string]: string | number | boolean | Object | undefined;
          },
          next: string
        ) => {
          return { ...prev, [next]: propertyAddress[next].value };
        },
        {}
      );

      const interestedProperties = [...data?.step2?.interestedProperties];
      interestedProperties.push({ id: uuidv4(), ...formattedData });
      setValue("step2.interestedProperties", interestedProperties);

      updateData({
        ...data,
        step2: {
          ...data.step2,
          interestedProperties,
        },
      });

      resetForm();
      toggleAddModal();
    }
  };

  const editAddressHandler = (address: any) => {
    toggleAddModal();
    setIsEditing(true);
    const formData = Object.keys(propertyAddress).reduce((prev, next) => {
      return {
        ...prev,
        [next]: {
          ...propertyAddress[next],
          value: address[next],
          isTouched: address[next] ? true : false,
        },
      };
    }, {});
    setPropertyAddress(formData);
    setEditAddressId(address.id);
  };

  const updateAddressHandler = () => {
    toggleAddModal();
    if (validateForm()) {
      const formattedData = Object.keys(propertyAddress).reduce(
        (
          prev: {
            [key: string]: string | number | boolean | Object | undefined;
          },
          next: string
        ) => {
          return {
            ...prev,
            [next]: propertyAddress[next].value,
            id: editAddressId,
          };
        },
        {}
      );

      const interestedPropertiesUpdated = data.step2.interestedProperties?.map(
        (ip: any) => {
          if (ip.id === editAddressId) {
            return { id: ip.id, ...formattedData };
          }
          return ip;
        }
      );
      setValue("step2.interestedProperties", interestedPropertiesUpdated);

      updateData({
        ...data,
        step2: {
          ...data.step2,
          interestedProperties: interestedPropertiesUpdated,
        },
      });

      resetForm();
      setIsEditing(false);
    }
  };

  const { upload } = useFileUpload({
    signAsset: signApplicationPublicAsset,
  });

  return (
    <div className="space-y-6 p-6 lg:col-span-8 rounded-lg">
      <h3 className="text-lg leading-6 font-medium text-primaryColor-normal">
        Real Estate Goals
      </h3>
      <div>
        <HookRadioGroup
          className="w-full"
          label="Have you identified a property ?"
          name="step2.q1"
          id="step2-q1"
          options={[
            { label: "Yes", value: "yes" },
            { label: "No", value: "no" },
          ]}
        />
      </div>
      {haveIdentifiedProperty === "yes" && (
        <div className="space-y-6">
          <div className="flex flex-row justify-between">
            <p className="block text-sm text-neutralColor-dark">
              Please list the address of any properties of interest.
            </p>
            <Button
              label="Add"
              type="button"
              btnStyle="primary"
              onClick={toggleAddModal}
              icon={<PlusIcon />}
            />
          </div>
          {showRemoveConfirm && (
            <Confirm
              title="Remove Development Address"
              message={`You are about to remove ${selectedAddress?.city} property. Are you sure you want to remove ?`}
              acceptButtonLabel="Remove"
              declineButtonLabel="Cancel"
              accept={confirmRemoveAddress}
              decline={cancelRemoveAddress}
            />
          )}
          {showAddModal && (
            <AddRealEstateGoalsForm
              isEditing={isEditing}
              address={propertyAddress}
              modalHandler={toggleAddModal}
              inputChangedHandler={inputChangedHandler}
              onAddAddressHandler={onAddAddressHandler}
              updateAddressHandler={updateAddressHandler}
            />
          )}
          {data?.step2?.interestedProperties?.length === 0 && (
            <div className="p-2 mt-2 flex flex-col items-center justify-center gap-2 h-2/6">
              <span>
                <InformationCircleIcon className="h-8 w-8 text-tertiaryColor-normal" />
              </span>
              <p>Property address not added</p>
            </div>
          )}
          {data?.step2?.interestedProperties &&
            data?.step2?.interestedProperties?.length > 0 && (
              <div className="space-y-2 w-full h-full ring-1 ring-neutralColor-lighter rounded-xl overflow-hidden">
                <table className="divide-y divide-gray-200 w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="p-4 text-left text-xs font-bold text-gray-500 capitalize tracking-wider">
                        S.No
                      </th>
                      <th className="p-4 text-left text-xs font-bold text-gray-500 capitalize tracking-wider">
                        Property Address
                      </th>
                      <th className="p-4 text-left text-xs font-bold text-gray-500 capitalize tracking-wider"></th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200 rounded-lg">
                    {data?.step2?.interestedProperties?.map(
                      (address: IInterestedProperty, index: number) => (
                        <tr
                          key={address.id}
                          className={`${
                            address.id === editAddressId
                              ? "bg-neutralColor-lighter"
                              : "cursor-pointer hover:bg-neutralColor-lighter"
                          }`}
                        >
                          <td className="p-4">{index + 1}</td>
                          <td className="p-4 capitalize">
                            {address.streetAddress}, {address.city},{" "}
                            {address.state}, {address.zipCode}
                          </td>
                          <td className="p-4 flex justify-end gap-4">
                            <div
                              className=" cursor-pointer"
                              onClick={() => editAddressHandler(address)}
                            >
                              <PencilAltIcon className="h-5 w-5 text-neutralColor-normal hover:text-blue-500" />
                            </div>
                            <div
                              className="cursor-pointer"
                              onClick={() => removeAddressHandler(address)}
                            >
                              <TrashIcon className="h-5 w-5 text-neutralColor-normal hover:text-tertiaryColor-normal" />
                            </div>
                          </td>
                        </tr>
                      )
                    )}
                  </tbody>
                </table>
              </div>
            )}
        </div>
      )}
      {haveIdentifiedProperty === "no" && (
        <HookInput
          customClassName="w-1/3"
          label="Tell us more about the use type your are imagining"
          name="step2.q1-interestedPropertyDetail"
          type="text"
        />
      )}
      <div>
        <HookRadioGroup
          label="Are you interested in a property that directly borders land you already own?"
          name="step2.q2"
          id="step2-q2"
          options={[
            { label: "Yes", value: "yes" },
            { label: "No", value: "no" },
          ]}
        />
      </div>
      <div className="flex gap-4 justify-end">
        <Button
          label="Previous"
          btnStyle="neutral"
          type="button"
          onClick={() => setCurrentStep((prev) => prev - 1)}
        />
        <Button label="Next" btnStyle="primary" type="submit" />
      </div>
    </div>
  );
}
